import {BehaviorSubject, Subscription} from "rxjs";
import {Directive, inject, OnDestroy, PLATFORM_ID} from "@angular/core";
import {AbstractControl, FormControl, FormGroup} from "@angular/forms";
import {BreakpointObserver} from "@angular/cdk/layout";
import {isPlatformBrowser} from "@angular/common";
import {required} from "@utility";

/// Directive annotation is here to avoid compile time error
/// https://stackoverflow.com/questions/63126067/class-is-using-angular-features-but-is-not-decorated-please-add-an-explicit-ang
@Directive()
export abstract class AbstractComponent implements OnDestroy {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly platformId = inject(PLATFORM_ID);

  protected subscriptions: Array<Subscription> = [];
  protected readonly isBrowser: boolean;

  protected isDesktop = true;
  protected isMobile = false;
  protected isMdScreen: boolean = false;

  protected readonly isMobileSubject = new BehaviorSubject<boolean>(false);
  public readonly isMobile$ = this.isMobileSubject.asObservable();

  protected readonly isMdScreenSubject = new BehaviorSubject<boolean>(false);
  public readonly isMdScreen$ = this.isMdScreenSubject.asObservable();


  constructor(
      desktopQuery: string = '(max-width: 767.98px)',
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.subscriptions.push(
          this.breakpointObserver.observe([desktopQuery]).subscribe(result => {
            this.isMobile = result.matches;
            this.isDesktop = !this.isMobile;
            this.isMobileSubject.next(this.isMobile);
          })
      );
      this.subscriptions.push(
          this.breakpointObserver.observe(['(min-width: 768px) and (max-width: 991.98px)']).subscribe(result => {
            this.isMdScreen = result.matches;
            this.isMdScreenSubject.next(this.isMdScreen);
          })
      );
    }
  }

  removeError(control: FormControl | FormGroup | AbstractControl, errorName: string): void {
    if (control.errors?.[errorName]) {
      delete control.errors?.[errorName]
    }
    if (Object.keys(control.errors ?? {}).length === 0) {
      control.setErrors(null);
    }
  }

  checkValidators(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const control: FormControl | FormGroup = formGroup.controls[key] as FormControl | FormGroup;
      if (control instanceof FormControl) {
        if (control.value === "") {
          control.setValue(undefined);
        }
      } else {
        this.checkValidators(control);
      }
    });
  }

  markAllAsTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const control: FormControl | FormGroup = formGroup.controls[key] as FormControl | FormGroup;
      if (control instanceof FormControl) {
        control.markAsTouched()
      } else {
        this.markAllAsTouched(control);
      }
    });
  }

  markAllAsUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const control: FormControl | FormGroup = formGroup.controls[key] as FormControl | FormGroup;
      if (control instanceof FormControl) {
        control.markAsUntouched()
      } else {
        this.markAllAsUntouched(control);
      }
    });
  }

  enableField = (formControl: FormControl, addRequired?: boolean, value?: any) => {
    formControl.markAsUntouched();
    formControl.enable();
    if (addRequired) formControl.addValidators([required]);
    if (value !== undefined) formControl.setValue(value);
    formControl.updateValueAndValidity();
  }

  disableField = (formControl: FormControl, value?: any) => {
    formControl.markAsUntouched();
    formControl.removeValidators([required]);
    formControl.setValue(value ?? null);
    formControl.disable();
    formControl.updateValueAndValidity();
  }

  /**
   * <p>Cleanup the resources claimed by component to avoid memory leaks.</p>
   */
  ngOnDestroy() {
    this.isMobileSubject.complete();
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }
}
